import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabSessions, useOpenLabAssistCase } from '../../hooks/lab-hooks';
import { LabAssistCase, LabAssistSeverity, LAB_ASSIST_SEVERITIES } from '../../support/labService';
import { LabOptionSelect, labErrorMessage } from './LabShared';

export type LabAssistOpenModalProps = {
  open: boolean;
  /** Fixed when raised from a run or a session page. */
  sessionKey?: string;
  runId?: number;
  deviceKey?: string;
  onClose: () => void;
  onOpened?: (assistCase: LabAssistCase) => void;
};

/**
 * Ask for help with a pod.
 *
 * Only a participant of the session can raise a case on it - the backend answers 404 for
 * anything else, so the session list here is the caller's own sessions rather than every
 * session that exists.
 */
const LabAssistOpenModal: React.FC<LabAssistOpenModalProps> = ({
  open,
  sessionKey,
  runId,
  deviceKey,
  onClose,
  onOpened,
}) => {
  const [session, setSession] = useState(sessionKey || '');
  const [subject, setSubject] = useState('');
  const [detail, setDetail] = useState('');
  const [severity, setSeverity] = useState<LabAssistSeverity>('normal');
  const [device, setDevice] = useState(deviceKey || '');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: sessions } = useLabSessions(false, { enabled: open && !sessionKey });
  const openCase = useOpenLabAssistCase();

  useEffect(() => {
    if (!open) return;

    setSession(sessionKey || '');
    setSubject('');
    setDetail('');
    setSeverity('normal');
    setDevice(deviceKey || '');
    setFormError(null);
    openCase.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionKey, deviceKey]);

  const handleSubmit = async () => {
    if (!session) {
      setFormError('Which pod?');
      return;
    }

    if (!subject.trim()) {
      setFormError('Give it a subject - it is what the queue shows.');
      return;
    }

    setFormError(null);

    try {
      const created = await openCase.mutateAsync({
        session_key: session,
        subject: subject.trim(),
        severity,
        ...(detail.trim() ? { detail: detail.trim() } : {}),
        ...(device.trim() ? { device_key: device.trim() } : {}),
        ...(runId ? { run_id: runId } : {}),
      });
      onOpened?.(created);
      onClose();
    } catch {
      // Surfaced through openCase.error.
    }
  };

  const submitError = formError || (openCase.isError ? labErrorMessage(openCase.error) : null);

  return (
    <StbModal
      open={open}
      role="save"
      title="Ask for help"
      size="large"
      submitLabel="Raise the case"
      submitDisabled={openCase.isPending}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {sessionKey ? (
          <div className="text-sm text-gray-600">{`Pod ${sessionKey}`}</div>
        ) : (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-gray-700">Pod</span>
            <select
              className="rounded border border-gray-300 p-2 text-sm"
              value={session}
              onChange={(evt) => setSession(evt.target.value)}
            >
              <option value="">Choose the session with the problem</option>
              {(sessions || []).map((candidate) => (
                <option value={candidate.session_key} key={candidate.session_key}>
                  {`${candidate.name || candidate.demo_name || candidate.session_key} (${candidate.session_key})`}
                </option>
              ))}
            </select>
          </label>
        )}

        <TextField
          label="Subject"
          value={subject}
          onChange={(evt) => setSubject(evt.target.value)}
          helperText="One line. What is wrong."
          fullWidth
        />

        <LabOptionSelect
          label="Severity"
          value={severity}
          options={LAB_ASSIST_SEVERITIES}
          onChange={(next) => setSeverity(next)}
        />

        <TextField
          label="Device (optional)"
          value={device}
          onChange={(evt) => setDevice(evt.target.value)}
          helperText="The device key, if it is one device. Groups cases by device across pods."
          fullWidth
        />

        <TextField
          label="Detail"
          value={detail}
          onChange={(evt) => setDetail(evt.target.value)}
          helperText="What you did, what you expected, what happened. Do not paste credentials."
          multiline
          minRows={4}
          fullWidth
        />
      </div>
    </StbModal>
  );
};

export default LabAssistOpenModal;
